import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useSession } from '@/entities/session'
import { TagChips } from '@/entities/tag'
import { type TaskListItem, tasksQueryOptions } from '@/entities/task'
import { useCompleteListedTask } from '@/features/edit-task'
import { useSearchStore } from '@/features/search'
import { Checkbox } from '@/shared/ui/checkbox'
import { Chip } from '@/shared/ui/chip'
import { DateStamp } from '@/shared/ui/date-stamp'
import { EmptyState } from '@/shared/ui/empty-state'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { ZoneNote } from '@/shared/ui/zone-badge'

// Глобальный список задач: сотруднику — все, заказчику — все задачи его сделок.
// Выполненные скрыты (open_only), закрытие чекбоксом оптимистично убирает
// строку. Контекст-строка линкует на компанию и проект, тайтл — на страницу задачи.
export function AllTaskList() {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const q = useQuery(tasksQueryOptions())
  const complete = useCompleteListedTask()

  function close(task: TaskListItem) {
    complete.mutate(
      { id: task.id, done: true },
      {
        onSuccess: () => toast.success('Задача закрыта'),
        onError: () => toast.error('Не удалось обновить'),
      },
    )
  }

  if (q.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (q.error) {
    return <StatusScreen title="Не удалось загрузить задачи" message="Обновите страницу." />
  }

  const tasks = q.data
  if (tasks.length === 0) {
    return <EmptyState title="Задач нет" description="Нет открытых задач." />
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Зона у моментов строк — один раз над списком; срок задачи ей не
          подписывается: это календарный день. */}
      <ZoneNote note="срок задачи — календарный день, без времени" />
      <ul className="flex flex-col gap-2">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} isEmployee={isEmployee} onClose={() => close(task)} />
        ))}
      </ul>
    </div>
  )
}

function TaskRow({
  task,
  isEmployee,
  onClose,
}: {
  task: TaskListItem
  isEmployee: boolean
  onClose: () => void
}) {
  return (
    <li
      data-testid="task-row"
      className="flex items-start gap-3 rounded-control bg-bg-surface px-4 py-3 ring-1 ring-border-subtle ring-inset"
    >
      <span className="mt-0.5">
        <Checkbox
          checked={task.done}
          aria-label={`Выполнить «${task.title}»`}
          onChange={(e) => {
            if (e.target.checked) onClose()
          }}
        />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link
          to="/$company/$project/tasks/$task"
          params={{ company: task.company.slug, project: task.project.slug, task: task.id }}
          className="truncate text-sm text-text-primary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          {task.title}
        </Link>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-text-tertiary text-xs">
          <Link
            to="/companies/$company"
            params={{ company: task.company.slug }}
            className="truncate hover:text-text-secondary hover:underline"
          >
            {task.company.name}
          </Link>
          <span aria-hidden>›</span>
          <Link
            to="/$company/$project"
            params={{ company: task.company.slug, project: task.project.slug }}
            className="truncate hover:text-text-secondary hover:underline"
          >
            {task.project.title}
          </Link>
          <span aria-hidden>·</span>
          <span>
            создана <DateStamp value={task.created_at} />
          </span>
          {task.due && (
            <>
              <span aria-hidden>·</span>
              <span>
                срок <DateStamp value={task.due} deadline />
              </span>
            </>
          )}
        </span>
        {task.tags.length > 0 && (
          <TagChips
            tags={task.tags}
            max={3}
            onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
          />
        )}
      </div>
      {isEmployee && task.blocking && (
        <Chip tone="accent" variant="outline">
          блокирует
        </Chip>
      )}
    </li>
  )
}
